import { useMutation, MutationFunction } from '@apollo/client';
import {
  Alert,
  Checkbox,
  Icon,
  Input,
  Tooltip,
  Select,
  Stack,
  Heading,
  Button as CommonUIButton,
  Modal,
  ModalHeader,
  ModalMain,
  ModalFooter,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil, notNil } from '@sixfold/typed-primitives';
import classNames from 'classnames';
import React, { useEffect, useRef, useState, useCallback } from 'react';

import { Captcha } from './captcha';
import { useIntegrationListSort } from './integration_list';
import { ManagedVehicleListContainer } from './managed_vehicle_list_container';
import { Button } from '../../components/button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { getEmbedConfig } from '../../lib/data';
import { Form as FormFactory } from '../../lib/form';
import {
  DeleteTelematicsIntegrationSyncStateMutation,
  DeleteTelematicsIntegrationSyncStateMutationVariables,
  PAGINATION_PAGE_SIZE,
  TelematicsIntegrationDiagnosticStatus,
  TelematicsIntegrationQuery,
  UpdateTelematicsIntegrationMutation,
  UpdateTelematicsIntegrationMutationVariables,
} from '../../lib/graphql';
import { sortToGraphql } from '../containers/integrations_container';
import { IntegrationInputField } from '../containers/provider_input_container';
import { ValidateIntegrationButton } from '../containers/telematics_validation_container';
import { deleteTelematicsIntegrationSyncStateMutation, telematicsIntegrationsListQuery } from '../graphql';
import { getConnectionData, TelematicsProviderIdentifier } from '../lib';

interface DeleteSyncStateProps {
  onClick: () => Promise<void>;
}

export interface IntegrationViewDataProps {
  data?: {
    telematicsIntegration: TelematicsIntegrationQuery['telematicsIntegration'];
    company_id: string;
  };
  sensitiveConnectionData?: Record<string, string | number | boolean | object> | null;
  sensitiveSyncStateData?: string | null;
}
export interface IntegrationViewProps {
  updateIntegration: MutationFunction<
    UpdateTelematicsIntegrationMutation,
    UpdateTelematicsIntegrationMutationVariables
  >;
  routeProps: {
    push: (path: string) => void;
    goBack: () => void;
  };
  addCaptchaToVariables: (captcha_token: string) => void;
}

class Form extends FormFactory<
  {
    telematicsProvider?: string;
    company_integration_id: string;
    enabled: boolean;
    diagnostic_status?: TelematicsIntegrationDiagnosticStatus | '';
    diagnostic_comment?: string;
  } & Record<string, string | number | boolean>
> {}

const SixfoldCarrierTelematicsApiWarning = () => (
  <Alert kind="warning">
    <strong>This integration is meant for Carrier API users</strong>
    <p>{"It is enabled / disabled by toggling the 'Enable Carrier Telematics API' feature flag"}</p>
  </Alert>
);

export const TtcIntegrationPurposeAlert = () => (
  <Alert kind="info">
    <p>
      {
        "Includes this carrier's VH tours in trimble-cloud-int-service to provide real-time visibility via TTC. Tours that already originate from TTC do not need this."
      }
    </p>
  </Alert>
);

export const TtcShipperIdAlert = () => (
  <Alert kind="info">
    <p>
      {
        "Set shipper_id to a shipper's VH company id to include tours between this carrier and that shipper. Leave empty to include tours with no shipper."
      }
    </p>
  </Alert>
);

export const DeleteSyncStateButton: React.FC<DeleteSyncStateProps> = ({ onClick }) => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  const onCancel = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onConfirm = useCallback(async () => {
    await onClick();
    if (isMounted.current) {
      setIsOpen(false);
    }
  }, [onClick]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <div>
      <Modal
        size="large"
        trigger={
          <CommonUIButton type="button" onClick={() => setIsOpen(true)} kind="danger" style={{ float: 'right' }}>
            Delete
          </CommonUIButton>
        }
        open={isOpen}
        onToggle={handleToggle}>
        <ModalHeader title="Are you sure?" />
        <ModalMain>
          <p>
            Please be aware that after clicking the confirm button, the current sync state data will be deleted{' '}
            <strong style={{ fontWeight: 'bold' }}>immediately</strong>.
          </p>
        </ModalMain>
        <ModalFooter>
          <Stack justifyContent="end">
            <CommonUIButton type="button" onClick={onCancel}>
              Cancel
            </CommonUIButton>
            <CommonUIButton iconEnd="small-check" kind="primary" onClick={onConfirm}>
              Confirm
            </CommonUIButton>
          </Stack>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const IntegrationView: React.FunctionComponent<IntegrationViewDataProps & IntegrationViewProps> = ({
  routeProps,
  updateIntegration,
  data,
  sensitiveConnectionData,
  sensitiveSyncStateData,
  addCaptchaToVariables,
}) => {
  const isNotAdmin = !useIsAdmin();
  const hasIntegrationsEditPermission = useHasScopes([BackofficeScope.telematicsWriteIntegrations]);
  const hasSensitiveDataReadingPermission = useHasScopes([BackofficeScope.telematicsReadSensitiveConnectionData]);
  const isPrivilegedUser = !isNotAdmin || hasIntegrationsEditPermission;
  const { sort } = useIntegrationListSort();

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [hasSyncStateData, setHasSyncStateData] = useState(notNil(sensitiveSyncStateData) ? true : false);

  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [deleteSyncState] = useMutation<
    DeleteTelematicsIntegrationSyncStateMutation,
    DeleteTelematicsIntegrationSyncStateMutationVariables
  >(deleteTelematicsIntegrationSyncStateMutation);

  const config = getEmbedConfig();
  if (config === null) {
    throw Error('Missing config');
  }

  if (data?.telematicsIntegration?.telematicsProvider === undefined) {
    return (
      <div>
        <p>Something went wrong, no data returned from server</p>
      </div>
    );
  }

  const { connectionParameters, telematicsProviderId } = data.telematicsIntegration.telematicsProvider;
  const connectionSettings = connectionParameters.reduce(
    (memo, current) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isNil(sensitiveConnectionData)
        ? (memo[current.fieldName] = notNil(data?.telematicsIntegration?.connection_data)
            ? data?.telematicsIntegration?.connection_data[current.fieldName]
            : null)
        : (memo[current.fieldName] = notNil(sensitiveConnectionData)
            ? sensitiveConnectionData[current.fieldName]
            : null);
      return memo;
    },
    {} as Record<string, unknown>,
  );

  const initialValues = {
    telematicsProvider: data.telematicsIntegration.telematics_provider_id,
    company_integration_id: data.telematicsIntegration.company_integration_id,
    enabled: data.telematicsIntegration.enabled,
    ...(data.telematicsIntegration.diagnosticStatus !== null
      ? { diagnostic_status: data.telematicsIntegration.diagnosticStatus }
      : {}),
    ...(data.telematicsIntegration.diagnosticComment !== null
      ? { diagnostic_comment: data.telematicsIntegration.diagnosticComment }
      : {}),
    ...connectionSettings,
  };

  const validateFields = connectionParameters
    .filter(({ isOptional }) => !isOptional)
    .reduce(
      (memo, current) => {
        memo[current.fieldName] = { exist: `Enter ${current.fieldName}` };
        return memo;
      },
      {} as Record<string, { exist: string }>,
    );

  const isUpdateDisabled =
    data.telematicsIntegration.isImmutable || !isPrivilegedUser || (isPrivilegedUser && !sensitiveConnectionData);

  const isManagedVehicles = data.telematicsIntegration.telematicsProvider.isVehicleManagementEnabled;

  return (
    <Stack>
      <div
        className={classNames({
          'company__integrations-left': true,
          'company__integrations-left-widthLimit': isManagedVehicles,
        })}>
        <Heading>Details</Heading>
        {telematicsProviderId === TelematicsProviderIdentifier.SixfoldCarrierTelematicsApi && (
          <SixfoldCarrierTelematicsApiWarning />
        )}
        {showCaptcha && (
          <Captcha
            onClickAway={() => setShowCaptcha(false)}
            onChange={(captcha_token) => {
              if (captcha_token !== null) {
                addCaptchaToVariables(captcha_token);
              }
            }}
          />
        )}
        <Form
          validateFields={validateFields}
          initialValues={initialValues}
          onSubmit={async (form) => {
            if (sensitiveConnectionData) {
              await updateIntegration({
                variables: {
                  input: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    telematics_integration_id: data.telematicsIntegration!.telematics_integration_id,
                    company_integration_id: form.company_integration_id,
                    enabled: form.enabled,
                    diagnosticStatus: form.diagnostic_status === '' ? null : form.diagnostic_status,
                    diagnosticComment: form.diagnostic_comment,
                    connection_data: getConnectionData({
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      connectionParameters: data.telematicsIntegration!.telematicsProvider!.connectionParameters,
                      formData: form,
                    }),
                  },
                },
                refetchQueries: [
                  {
                    query: telematicsIntegrationsListQuery,
                    variables: {
                      companyId: data.company_id,
                      limit: PAGINATION_PAGE_SIZE,
                      sort: sortToGraphql(sort),
                    },
                  },
                ],
              });
              if (isMounted.current) {
                routeProps.push(`/companies/${data.company_id}/integrations`);
              }
            } else {
              if (isMounted.current) {
                setShowCaptcha(true);
              }
            }
          }}>
          {({ form, onChange, errors, onSubmit, isSubmittingForm }) => {
            return (
              <form onSubmit={onSubmit}>
                <Stack direction="column" gap="large" style={{ maxWidth: 500 }}>
                  <Stack direction="column" gap="small">
                    <Input
                      label="Telematics provider"
                      name="telematicsProvider"
                      value={data.telematicsIntegration?.telematicsProvider?.telematicsProviderName ?? ''}
                      disabled
                    />
                    {telematicsProviderId === TelematicsProviderIdentifier.Ttc && <TtcIntegrationPurposeAlert />}
                  </Stack>

                  {data.telematicsIntegration?.telematicsProvider?.connectionParameters.map((setting) => {
                    const displayName = setting.fieldDisplayName ?? setting.fieldName;
                    const hasError = errors[setting.fieldName] !== undefined;

                    const labelText = hasError
                      ? (errors[setting.fieldName] ?? displayName)
                      : `${displayName}${setting.isOptional ? ' (optional)' : ''}${
                          setting.isInternal ? ' (internal)' : ''
                        }`;

                    return (
                      <Stack key={setting.fieldName} direction="column" gap="small">
                        <IntegrationInputField
                          label={labelText}
                          fieldType={setting.fieldType}
                          settingsValue={(form[setting.fieldName] ?? '').toString()}
                          fieldName={setting.fieldName}
                          disabled={isUpdateDisabled}
                          defaultChecked={form[setting.fieldName] === true}
                          enumFieldValues={setting.enumFieldValues ?? []}
                          onChange={onChange}
                          hasError={hasError}
                        />
                        {setting.fieldName === 'shipper_id' &&
                          telematicsProviderId === TelematicsProviderIdentifier.Ttc && <TtcShipperIdAlert />}
                      </Stack>
                    );
                  })}
                  {form.telematicsProvider !== undefined && (
                    <Input
                      label="Company integration id"
                      name="company_integration_id"
                      value={form.company_integration_id}
                      disabled={isUpdateDisabled}
                      onChange={({ value }) => onChange({ value, name: 'company_integration_id' })}
                    />
                  )}
                  {form.telematicsProvider !== undefined && (
                    <Stack direction="row" gap="xsmall" alignItems="center">
                      <Select
                        label="Diagnostic status"
                        labelPosition="block"
                        isClearable
                        placeholder="-"
                        options={[
                          {
                            value: TelematicsIntegrationDiagnosticStatus.INTERMITTENT,
                            label: TelematicsIntegrationDiagnosticStatus.INTERMITTENT,
                          },
                          {
                            value: TelematicsIntegrationDiagnosticStatus.INVESTIGATING,
                            label: TelematicsIntegrationDiagnosticStatus.INVESTIGATING,
                          },
                          {
                            value: TelematicsIntegrationDiagnosticStatus.INVALID_CREDENTIALS,
                            label: TelematicsIntegrationDiagnosticStatus.INVALID_CREDENTIALS,
                          },
                          {
                            value: TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR,
                            label: TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR,
                          },
                        ]}
                        value={form.diagnostic_status || null}
                        disabled={isUpdateDisabled}
                        onChange={(option) => {
                          onChange({ value: option === null ? '' : option.value, name: 'diagnostic_status' });
                        }}
                      />
                      <Tooltip
                        content={
                          <span style={{ whiteSpace: 'pre-line' }}>
                            {`${TelematicsIntegrationDiagnosticStatus.INTERMITTENT} - the integration is operating less than optimally. For example, GPS data might be sent inconsistently, resulting in GPS data gaps.\n\n${TelematicsIntegrationDiagnosticStatus.INVESTIGATING} - the integration is not operational and needs to be investigated from our side. Usually means that the integration should also be disabled.\n\n${TelematicsIntegrationDiagnosticStatus.INVALID_CREDENTIALS} - the integration is marked as having invalid credentials either manually or automatically by alert service.\n\n${TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR} - the provider is not operational and needs to be investigated from our side.`}
                          </span>
                        }>
                        <Icon name="help" size={12} />
                      </Tooltip>
                    </Stack>
                  )}
                  {form.telematicsProvider !== undefined && (
                    <Input
                      label="Diagnostic comment (NB! Visible to customer. Please do not add here any internal or sensitive data)"
                      name="diagnostic_comment"
                      value={form.diagnostic_comment}
                      disabled={isUpdateDisabled}
                      onChange={({ value }) => onChange({ value, name: 'diagnostic_comment' })}
                    />
                  )}
                  {form.telematicsProvider !== undefined && hasSensitiveDataReadingPermission && hasSyncStateData && (
                    <div className="field">
                      <label>Sync state</label>
                      <div className="sync-state-box">
                        <div>
                          {sensitiveSyncStateData
                            ? JSON.stringify(JSON.parse(sensitiveSyncStateData), null, 2).replace(/\\n/g, '\n')
                            : null}
                        </div>
                        <div className="delete-sync-state-button">
                          <DeleteSyncStateButton
                            onClick={async () => {
                              if (sensitiveSyncStateData) {
                                await deleteSyncState({
                                  variables: {
                                    telematics_integration_id:
                                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      data.telematicsIntegration!.telematics_integration_id,
                                  },
                                });
                                if (isMounted.current) {
                                  setHasSyncStateData(false);
                                }
                              } else {
                                if (isMounted.current) {
                                  setShowCaptcha(true);
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {form.telematicsProvider !== undefined && (
                    <Checkbox
                      label="Enabled"
                      name="enabled"
                      checked={form.enabled}
                      disabled={isUpdateDisabled}
                      onChange={({ checked }) => onChange({ value: checked, name: 'enabled' })}
                    />
                  )}
                </Stack>
                {form.telematicsProvider !== undefined && (
                  <div className="integration__configuration-footer">
                    <Stack gap="small">
                      {!data?.telematicsIntegration?.isImmutable && isPrivilegedUser && !sensitiveConnectionData ? (
                        <Button type="button" kind="primary" onClick={() => setShowCaptcha(true)}>
                          Start updating
                        </Button>
                      ) : (
                        <Button type="submit" kind="primary" disabled={isUpdateDisabled || isSubmittingForm}>
                          Update
                        </Button>
                      )}
                      <Button type="button" onClick={() => routeProps.goBack()}>
                        Cancel
                      </Button>
                    </Stack>
                    <ValidateIntegrationButton
                      integration={
                        !isPrivilegedUser
                          ? {
                              telematics_provider_id: form.telematicsProvider,
                              telematics_integration_id: data.telematicsIntegration?.telematics_integration_id,
                            }
                          : {
                              telematics_provider_id: form.telematicsProvider,
                              connection_data: getConnectionData({
                                connectionParameters:
                                  data.telematicsIntegration!.telematicsProvider.connectionParameters, // eslint-disable-line @typescript-eslint/no-non-null-assertion
                                formData: form,
                              }),
                            }
                      }
                    />
                  </div>
                )}
              </form>
            );
          }}
        </Form>
      </div>
      <div className="company__integrations-right">
        {isManagedVehicles && (
          <ManagedVehicleListContainer
            telematics_integration_id={
              data.telematicsIntegration.telematics_integration_id
            }></ManagedVehicleListContainer>
        )}
      </div>
    </Stack>
  );
};
